import type {Mapper} from '@automapper/core';
import {constructUsing, createMap, forMember, mapFrom} from '@automapper/core';
import type {DebtResponsibleMasterAgent} from '@igp/shared/contract/pam';
import {formatTimestamp} from '@igp/shared/utils';

import type {
    DebtResponsibleMasterAgentDownloadFields,
    DebtResponsibleMasterAgentDownloadModel,
    DebtResponsibleMasterAgentQueryFields,
    DebtResponsibleMasterAgentSortKeys,
    DebtResponsibleMasterAgentViewModel,
    DebtResponsibleMasterAgentViewModelKeys,
} from './types';

export const debtResponsibleMasterAgentMappingKeys = {
    serverModel: 'DebtResponsibleMasterAgent',
    viewModel: 'DebtResponsibleMasterAgentViewModel',
    downloadModel: 'DebtResponsibleMasterAgentDownloadModel',
    viewModelKeys: 'DebtResponsibleMasterAgentViewModelKeys',
    queryFields: 'DebtResponsibleMasterAgentQueryFields',
    sortKeys: 'DebtResponsibleMasterAgentSortKeys',
    downloadFields: 'DebtResponsibleMasterAgentDownloadFields',
} as const;

export class DebtResponsibleMasterAgentMapper {
    createMapping(mapper: Mapper): void {
        createMap<DebtResponsibleMasterAgent, DebtResponsibleMasterAgentViewModel>(
            mapper,
            debtResponsibleMasterAgentMappingKeys.serverModel,
            debtResponsibleMasterAgentMappingKeys.viewModel,
            forMember(
                vm => vm.agent_uid,
                mapFrom(m => m.agent_id)
            ),
            forMember(
                vm => vm.aggregated_downline_debt,
                mapFrom(m => m.aggregated_downline_debt)
            ),
            forMember(
                vm => vm.created_at,
                mapFrom(m => m.created_at)
            ),
            forMember(
                vm => vm.created_by,
                mapFrom(m => m.created_by)
            ),
            forMember(
                vm => vm.downline_agents_count,
                mapFrom(m => m.downline_agents_count)
            ),
            forMember(
                vm => vm.username,
                mapFrom(m => m.username)
            )
        );
        createMap<DebtResponsibleMasterAgent, DebtResponsibleMasterAgentDownloadModel>(
            mapper,
            debtResponsibleMasterAgentMappingKeys.serverModel,
            debtResponsibleMasterAgentMappingKeys.downloadModel,
            forMember(
                vm => vm.agent_uid,
                mapFrom(m => m.agent_id)
            ),
            forMember(
                vm => vm.aggregated_downline_debt,
                mapFrom(m => m.aggregated_downline_debt?.toFixed(2))
            ),
            forMember(
                vm => vm.created_at,
                mapFrom(m => formatTimestamp(m.created_at, 'date', false))
            ),
            forMember(
                vm => vm.created_by,
                mapFrom(m => m.created_by)
            ),
            forMember(
                vm => vm.downline_agents_count,
                mapFrom(m => m.downline_agents_count?.toString())
            ),
            forMember(
                vm => vm.username,
                mapFrom(m => m.username)
            )
        );
        createMap<DebtResponsibleMasterAgentViewModelKeys, DebtResponsibleMasterAgentQueryFields>(
            mapper,
            debtResponsibleMasterAgentMappingKeys.viewModelKeys,
            debtResponsibleMasterAgentMappingKeys.queryFields,
            constructUsing(m => {
                const mapper: Record<DebtResponsibleMasterAgentViewModelKeys, DebtResponsibleMasterAgentQueryFields> = {
                    agent_uid: 'items.agent_id',
                    aggregated_downline_debt: 'items.aggregated_downline_debt',
                    created_at: 'items.created_at.seconds',
                    created_by: 'items.created_by',
                    downline_agents_count: 'items.downline_agents_count',
                    username: 'items.username',
                };
                return mapper[m];
            })
        );
        createMap<DebtResponsibleMasterAgentViewModelKeys, DebtResponsibleMasterAgentSortKeys>(
            mapper,
            debtResponsibleMasterAgentMappingKeys.viewModelKeys,
            debtResponsibleMasterAgentMappingKeys.sortKeys,
            constructUsing(m => {
                const mapper: Record<DebtResponsibleMasterAgentViewModelKeys, DebtResponsibleMasterAgentSortKeys> = {
                    agent_uid: 'agent_id',
                    aggregated_downline_debt: 'aggregated_downline_debt',
                    created_at: 'created_at',
                    created_by: 'created_by',
                    downline_agents_count: 'downline_agents_count',
                    username: 'username',
                };
                return mapper[m];
            })
        );
        createMap<DebtResponsibleMasterAgentSortKeys, DebtResponsibleMasterAgentViewModelKeys>(
            mapper,
            debtResponsibleMasterAgentMappingKeys.sortKeys,
            debtResponsibleMasterAgentMappingKeys.viewModelKeys,
            constructUsing(m => {
                const mapper: Record<DebtResponsibleMasterAgentSortKeys, DebtResponsibleMasterAgentViewModelKeys> = {
                    agent_id: 'agent_uid',
                    aggregated_downline_debt: 'aggregated_downline_debt',
                    created_at: 'created_at',
                    created_by: 'created_by',
                    downline_agents_count: 'downline_agents_count',
                    username: 'username',
                };
                return mapper[m];
            })
        );
        createMap<DebtResponsibleMasterAgentViewModelKeys, DebtResponsibleMasterAgentDownloadFields>(
            mapper,
            debtResponsibleMasterAgentMappingKeys.viewModelKeys,
            debtResponsibleMasterAgentMappingKeys.downloadFields,
            constructUsing(m => {
                const mapper: Record<DebtResponsibleMasterAgentViewModelKeys, DebtResponsibleMasterAgentDownloadFields> = {
                    agent_uid: 'agent_id',
                    aggregated_downline_debt: 'aggregated_downline_debt',
                    created_at: 'created_at.seconds',
                    created_by: 'created_by',
                    downline_agents_count: 'downline_agents_count',
                    username: 'username',
                };
                return mapper[m];
            })
        );
    }
}
